import { http } from "@shared/utils/request";
import type { FetchListOptions, ListResult } from "@shared/types/list";
import type { CurrentUserInfoData, UserMenuItem, UserMenusData } from "@shared/types/user";
import type { SystemProfileDetail, UpdateProfilePayload } from "@app/views/system/personalCenter/types";
import { mapUserDetailToProfile } from "@app/views/system/personalCenter/mapUserDetail";
import { toUserListRequestParams } from "@app/views/system/accountUser/config/filter";
import { mapUserListItemToRow } from "@app/views/system/accountUser/mapUserListItem";
import type { SystemUserQueryParam, SystemUserRow, UserListApiItem } from "@app/views/system/accountUser/types";

/** 当前登录用户详情 */
export function fetchCurrentUserInfo(): Promise<CurrentUserInfoData> {
    return http.get<CurrentUserInfoData>("/admin/user/info");
}

/** 当前登录用户菜单 */
export function fetchCurrentUserMenus(): Promise<UserMenuItem[]> {
    return http.get<UserMenusData>("/admin/menus").then(res => res.items ?? []);
}

/** 修改当前登录用户密码 */
export function changeCurrentPassword(oldPassword: string, password: string): Promise<void> {
    return http.post<void>("/admin/user/changePassword", { oldPassword, password });
}

/** 更新当前登录用户资料（用户名 / 昵称 / 邮箱 / 头像 URL） */
export function updateCurrentProfile(payload: UpdateProfilePayload): Promise<void> {
    return http.post<void>("/admin/user/updateProfile", payload);
}

/** 个人中心：加载资料 */
export async function loadCurrentProfile(): Promise<SystemProfileDetail> {
    const data = await fetchCurrentUserInfo();
    return mapUserDetailToProfile(data.userDetail);
}

interface UserListApiData {
    items: UserListApiItem[];
    total: number;
}

/** 本企业用户列表 */
export function fetchUserList(params: SystemUserQueryParam, options?: FetchListOptions): Promise<ListResult<SystemUserRow>> {
    return http.get<UserListApiData>("/admin/user/list", toUserListRequestParams(params), { signal: options?.signal }).then(res => ({
        list: (res.items || []).map(mapUserListItemToRow),
        total: res.total ?? 0,
    }));
}

/** 客户企业用户列表（进入客户端）。corpIds 为空表示当前主企业下全部客户企业 */
export function fetchClientUserList(params: SystemUserQueryParam & { corpIds?: string[] }, options?: FetchListOptions): Promise<ListResult<SystemUserRow>> {
    const body = {
        ...toUserListRequestParams(params),
        corpIds: params.corpIds ?? [],
    };
    return http.post<UserListApiData>("/admin/user/clientList", body, { signal: options?.signal }).then(res => ({
        list: (res.items || []).map(mapUserListItemToRow),
        total: res.total ?? 0,
    }));
}

/** 签发进入客户端的一次性 ticket */
export function jumpLoginUser(userId: string): Promise<{ ticket: string }> {
    return http.post<{ ticket: string }>("/admin/jumpLogin", { userId });
}
